import React, { useState, useEffect, useRef } from 'react';
import { Card, CardContent } from './ui/card';
import { benefitsData } from '../data/mock';
import * as LucideIcons from 'lucide-react';
import { Sparkles } from 'lucide-react';
import { Pyramid3D, Hexagon3D, Sphere3D, IsometricContainer } from './3DAssets';

const BenefitsSection = () => {
  const [visibleCards, setVisibleCards] = useState([]);
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            const index = parseInt(entry.target.dataset.index);
            setVisibleCards(prev => [...new Set([...prev, index])]);
          }
        });
      },
      { threshold: 0.2 }
    );

    const cards = document.querySelectorAll('.benefit-card');
    cards.forEach(card => observer.observe(card));

    return () => {
      cards.forEach(card => observer.unobserve(card));
    };
  }, []);

  return (
    <div ref={sectionRef} className="py-32 bg-white relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 opacity-40">
        <div className="absolute top-0 left-0 w-full h-full bg-[radial-gradient(circle_at_30%_20%,rgba(139,58,58,0.05)_0%,transparent_50%)]"></div>
        <div className="absolute top-0 right-0 w-full h-full bg-[radial-gradient(circle_at_70%_80%,rgba(74,85,104,0.05)_0%,transparent_50%)]"></div>
      </div>

      {/* 3D Decorative Assets */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-[10%] right-[10%] opacity-15">
          <Pyramid3D color="#8B3A3A" size={90} />
        </div>
        <div className="absolute bottom-[15%] left-[8%] opacity-20">
          <Hexagon3D color="#6B2A2A" size={110} />
        </div>
        <div className="absolute top-[50%] left-[45%] opacity-10">
          <Sphere3D color="#4A5568" size={70} />
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative z-10">
        {/* Section Header */}
        <div className="text-center mb-20">
          <div className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8B3A3A]/10 to-[#4A5568]/10 backdrop-blur-sm px-6 py-3 rounded-full border border-[#8B3A3A]/20 mb-6">
            <Sparkles className="w-4 h-4 text-[#8B3A3A]" />
            <span className="text-sm font-semibold text-slate-700">Why Choose Us</span>
          </div>
          <h2 className="text-5xl md:text-6xl font-black text-slate-900 mb-6">
            Built For Your Success
          </h2>
          <p className="text-xl text-slate-600 max-w-3xl mx-auto leading-relaxed">
            We combine proven expertise, cutting-edge technology, and a thriving global community to accelerate your professional growth.
          </p>
        </div>

        {/* Benefits Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
          {benefitsData.map((benefit, index) => {
            const IconComponent = LucideIcons[benefit.icon];
            const isVisible = visibleCards.includes(index);

            return (
              <Card 
                key={benefit.id}
                data-index={index}
                className={`benefit-card group relative bg-white/80 backdrop-blur-sm border-2 border-slate-200/50 hover:border-[#8B3A3A]/50 hover:shadow-2xl transition-all duration-500 overflow-hidden transform-3d ${
                  isVisible ? 'opacity-100 scale-100' : 'opacity-0 scale-90'
                }`}
                style={{ 
                  transitionDelay: `${index * 100}ms`,
                  transform: `perspective(1000px) ${isVisible ? 'translateZ(0px) rotateX(0deg)' : 'translateZ(-50px) rotateX(-10deg)'}`
                }}
              >
                {/* Gradient Background on Hover */}
                <div className="absolute inset-0 bg-gradient-to-br from-[#8B3A3A]/0 to-[#6B2A2A]/0 group-hover:from-[#8B3A3A]/5 group-hover:to-[#6B2A2A]/5 transition-all duration-500"></div>
                
                <CardContent className="p-8 text-center relative z-10" style={{ transformStyle: 'preserve-3d' }}>
                  {/* Icon */}
                  <div className="mb-8 flex justify-center">
                    <IsometricContainer color={index === 0 ? "#8B3A3A" : index === 1 ? "#4A5568" : index === 2 ? "#6B2A2A" : "#8B3A3A"}>
                      {IconComponent && <IconComponent className="w-8 h-8 text-white" />}
                    </IsometricContainer>
                  </div>

                  {/* Title */}
                  <h3 
                    className="text-2xl font-black text-slate-900 mb-4 group-hover:text-[#8B3A3A] transition-colors duration-500"
                    style={{ transform: 'translateZ(30px)', transformStyle: 'preserve-3d' }}
                  >
                    {benefit.title}
                  </h3>

                  {/* Description */}
                  <p 
                    className="text-slate-600 leading-relaxed"
                    style={{ transform: 'translateZ(20px)', transformStyle: 'preserve-3d' }}
                  >
                    {benefit.description}
                  </p>

                  {/* Decorative Line */}
                  <div 
                    className="mt-6 h-1 w-0 bg-gradient-to-r from-[#8B3A3A] to-[#6B2A2A] group-hover:w-full transition-all duration-500 mx-auto rounded-full"
                    style={{ transform: 'translateZ(35px)', transformStyle: 'preserve-3d' }}
                  ></div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default BenefitsSection;
